import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { rxObjectify } from 'osn-shared';
import { forkJoin, Observable, of } from 'rxjs';
import { first, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { RootAppState } from '../app.reducer';
import { selectCxbCountries, selectCxbPlatforms, selectCxbSelectedTenant, selectCxbUserSegments } from '../app.selectors';
import {
  SimpleContentPage,
  SimpleGenrePage,
  SimpleMarketingPage,
  SimplePagesContainer,
  SimplePageType,
  SimpleRedeemPage,
  SimplePage,
} from '../domain/simple-page';
import { DEFAULT_PAGINATION_LIMIT } from '../shared/app-constants';
import { ContentPageDetailDialogDataViewModel } from '../shared/components/content-page-detail-dialog/content-page-detail-dialog-data-view.model';
import { RedeemPageDetailDialogDataViewModel } from '../shared/components/redeem-page-detail-dialog/redeem-page-detail-dialog-data.view-model';
import {
  mapContentApiPageToDomain,
  mapCreateContentPageModelToApi,
  mapCreateRedeemPageModelToApi,
  mapGenreApiPageToDomain,
  mapMarketingApiPageToDomain,
  mapRedeemApiPageToDomain,
} from './mapping/map-page-api-to-domain';
import { RedeemPageDataService, GenrePageDataService, CreateGenrePageApiModel, PageDataService } from '@cxb/editor-stb-api';

@Injectable({ providedIn: 'root' })
export class PageApiService {
  constructor(
    private readonly redeemPageDataService: RedeemPageDataService,
    private readonly contentPageDataService: PageDataService,
    private readonly genrePageDataService: GenrePageDataService,
    private readonly rootStore: Store<RootAppState>
  ) {}

  public listPagesOfType$<T extends SimplePageType>(pageType: T): Observable<SimplePage[]> {
    return this.rootStore.select(selectCxbSelectedTenant).pipe(
      switchMap((selectedTenant) => {
        switch (pageType) {
          case 'Content':
            return this.contentPageDataService
              .filterPages(undefined, undefined, DEFAULT_PAGINATION_LIMIT, undefined, selectedTenant?.id)
              .pipe(map((result) => result.map(mapContentApiPageToDomain)));
          case 'Genre':
            return forkJoin([
              this.rootStore.select(selectCxbCountries).pipe(first((it) => it.length > 0)),
              this.rootStore.select(selectCxbUserSegments).pipe(first((it) => it.length > 0)),
              this.genrePageDataService.getGenrePages(selectedTenant?.id),
            ]).pipe(map(([countries, userSegments, genrePages]) => genrePages.map((it) => mapGenreApiPageToDomain(it))));
          case 'Marketing':
            return this.contentPageDataService
              .filterMarketingPages(selectedTenant?.id)
              .pipe(map((result) => result.map(mapMarketingApiPageToDomain)));
          case 'Redeem':
            return forkJoin([
              this.rootStore.select(selectCxbCountries).pipe(first((it) => it.length > 0)),
              this.rootStore.select(selectCxbUserSegments).pipe(first((it) => it.length > 0)),
              this.redeemPageDataService.filterRedeemPages(null, null, selectedTenant?.id),
            ]).pipe(
              map(([countries, userSegments, redeemPages]) =>
                redeemPages.map((it) => mapRedeemApiPageToDomain(countries, userSegments, it))
              )
            );
        }
      }),
      take(1)
    );
  }

  public listAllPages$(): Observable<SimplePagesContainer> {
    return forkJoin([
      this.listPagesOfType$('Content'),
      this.listPagesOfType$('Marketing'),
      this.listPagesOfType$('Redeem'),
      this.listPagesOfType$('Genre'),
    ]).pipe(
      rxObjectify('contentPages', 'marketingPages', 'redeemPages', 'genrePages'),
      map((result) => ({
        contentPages: result.contentPages as SimpleContentPage[],
        marketingPages: result.marketingPages as SimpleMarketingPage[],
        redeemPages: result.redeemPages as SimpleRedeemPage[],
        genrePages: result.genrePages as SimpleGenrePage[],
      }))
    );
  }

  public createRedeemPage$(data: RedeemPageDetailDialogDataViewModel): Observable<SimpleRedeemPage> {
    return of(data).pipe(
      withLatestFrom(this.rootStore.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => {
        return this.rootStore.select(selectCxbPlatforms).pipe(
          first((it) => it.length > 0),
          map((platforms) => platforms.map((platform) => platform.id)),
          switchMap((platformIds) => {
            return forkJoin([
              this.rootStore.select(selectCxbCountries).pipe(first((it) => it.length > 0)),
              this.rootStore.select(selectCxbUserSegments).pipe(first((it) => it.length > 0)),
              this.redeemPageDataService.createRedeemPage(mapCreateRedeemPageModelToApi(data, platformIds, selectedTenant?.id)),
            ]).pipe(map(([countries, userSegments, redeemPage]) => mapRedeemApiPageToDomain(countries, userSegments, redeemPage)));
          })
        );
      })
    );
  }

  public createContentPage$(data: ContentPageDetailDialogDataViewModel): Observable<SimpleContentPage> {
    return of(data).pipe(
      withLatestFrom(this.rootStore.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => {
        return this.contentPageDataService
          .createPage(mapCreateContentPageModelToApi(data, selectedTenant?.id))
          .pipe(map((result) => mapContentApiPageToDomain(result)));
      })
    );
  }

  public createGenrePage$(data: ContentPageDetailDialogDataViewModel): Observable<SimpleGenrePage> {
    return of(data).pipe(
      withLatestFrom(this.rootStore.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => {
        return this.genrePageDataService
          .createGenrePage(mapCreateContentPageModelToApi(data, selectedTenant?.id) as CreateGenrePageApiModel)
          .pipe(map((result) => mapGenreApiPageToDomain(result)));
      })
    );
  }
}
